import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  GetAllOrders,
  StatusUpdate,
  UpdatePaymentStatus,
  ProcessRefund,
  GetAvailableDeliveryPart,
  AssignDeliveryBoy,
  GetMyStoreOrders,
  GetOrdersPage,
  GetOrderMetrics,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { subscribeToOrderChanges } from "../../helpers/firebaseRealtime";
import classes from "./styles.module.css";

// ─── Reason Options ───────────────────────────────────────────────────────────
const CANCEL_REASONS = [
  "Customer Requested Cancellation",
  "Store Closed",
  "Power Failure",
  "Kitchen Equipment Failure",
  "Ingredient Out Of Stock",
  "Delivery Partner Unavailable",
  "Duplicate Order",
  "Payment Issue",
  "Other",
];

const REJECT_REASONS = [
  "Store Closed",
  "Kitchen Closed",
  "Item Out Of Stock",
  "Restaurant Busy",
  "Payment Verification Failed",
  "Customer Location Outside Delivery Area",
  "Suspicious Order",
  "Duplicate Order",
  "Other",
];

const DELIVERY_FAILED_REASONS = [
  "Customer Not Reachable",
  "Customer Didn't Pick Up",
  "Wrong Address",
  "Customer Refused Delivery",
  "Store Delay",
  "Vehicle Breakdown",
  "Weather Conditions",
  "Delivery Boy Emergency",
  "Other",
];

// ─── Reusable Reason Modal ────────────────────────────────────────────────────
const ReasonModal = ({
  isOpen,
  toggle,
  title,
  reasons,
  commentsLabel,
  commentsRequired,
  onConfirm,
  confirmLabel,
  confirmColor,
}) => {
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");

  const handleConfirm = () => {
    if (!reason) return toast.error("Please select a reason.");
    if (commentsRequired && !comments.trim())
      return toast.error("Comments are required.");
    onConfirm({ reason, comments });
    setReason("");
    setComments("");
  };

  const handleToggle = () => {
    setReason("");
    setComments("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="md">
      <ModalHeader toggle={handleToggle} style={{ borderBottom: "2px solid #f1f3f5" }}>
        <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>{title}</span>
      </ModalHeader>
      <ModalBody style={{ padding: "1.5rem" }}>
        <FormGroup>
          <Label style={{ fontWeight: 700, fontSize: "0.88rem", color: "#343a40" }}>
            Reason <span style={{ color: "#e31b23" }}>*</span>
          </Label>
          <Input
            type="select"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{
              borderRadius: "10px",
              borderColor: reason ? "#f57c00" : "#dee2e6",
              fontSize: "0.9rem",
              padding: "0.65rem 1rem",
              boxShadow: "none",
            }}
          >
            <option value="">▼ Select a reason...</option>
            {reasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup style={{ marginTop: "1rem" }}>
          <Label style={{ fontWeight: 700, fontSize: "0.88rem", color: "#343a40" }}>
            {commentsLabel || "Comments"}
            {commentsRequired && (
              <span style={{ color: "#e31b23" }}> *</span>
            )}
            {!commentsRequired && (
              <span style={{ color: "#868e96", fontWeight: 400 }}> (optional)</span>
            )}
          </Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Add any additional notes here..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            style={{
              borderRadius: "10px",
              borderColor: "#dee2e6",
              fontSize: "0.9rem",
              resize: "none",
              boxShadow: "none",
            }}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter style={{ borderTop: "2px solid #f1f3f5", gap: "0.5rem" }}>
        <Button
          onClick={handleConfirm}
          style={{
            backgroundColor: confirmColor || "#e31b23",
            borderColor: confirmColor || "#e31b23",
            fontWeight: 700,
            borderRadius: "10px",
            padding: "0.5rem 1.5rem",
          }}
        >
          {confirmLabel || "Confirm"}
        </Button>
        <Button
          color="secondary"
          onClick={handleToggle}
          style={{ borderRadius: "10px", fontWeight: 600 }}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Orders still "in transition" — anything that hasn't reached a terminal
// state yet. Used by the "Live Orders" tab/route so a store admin can see
// only what still needs attention, without the noise of completed history.
const LIVE_ORDER_TERMINAL_STATUSES = ["delivered", "rejected", "cancelled", "canceled"];

// ─── Main Component ───────────────────────────────────────────────────────────
const Orders = ({ defaultTab }) => {
  document.title = "Orders | Susipizza";

  // Browse mode (no search query): server-paginated, 20 orders per page,
  // fetched only when that page is actually requested — replaces the old
  // "load the whole (capped) order list up front" pattern.
  const [pageOrders, setPageOrders] = useState([]);
  const [pageTotalCount, setPageTotalCount] = useState(0);
  // page number -> cursor to use to fetch THAT page (page 1 -> null).
  // Grows as the admin navigates forward; reset on tab change.
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    newOrders: 0,
    preparingOrders: 0,
    liveOrders: 0,
    totalRevenue: 0,
  });

  // Search mode: a non-empty search box bypasses pagination entirely and
  // does one full (still capped at 500 server-side) fetch, filtered/paginated
  // client-side — same behavior as before this change, just scoped to only
  // fire when actively searching rather than being the default browse path.
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [AvailableDeliveryBoys, setAvailableDeliveryBoys] = useState([]);
  const [selectedDeliveryBoyName, setSelectedDeliveryBoyName] = useState("");
  const [selectedDeliveryBoyPhoneNumber, setSelectedDeliveryBoyPhoneNumber] =
    useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(defaultTab || "all");

  // /Orders and /Orders/live render this same component at the same tree
  // position, so React updates props on the existing instance rather than
  // remounting it — the useState initializer above only runs once, on true
  // first mount, so it misses every subsequent navigation between the two.
  // This keeps the tab in sync whenever defaultTab itself changes.
  useEffect(() => {
    setActiveTab(defaultTab || "all");
  }, [defaultTab]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Refs so the polling/RTDB effects below (deliberately empty-dep, so they
  // don't resubscribe/reschedule on every render) always act on the
  // *current* page/mode/tab rather than whatever was true when they first
  // mounted. Assigned directly during render (not via a separate useEffect)
  // so there's no dependency on effect-execution order relative to other
  // same-deps effects — the ref is always current by the time anything
  // else runs.
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // ── Reason Modal States ──
  const [rejectModal, setRejectModal] = useState({ open: false, orderId: null });
  const [cancelModal, setCancelModal] = useState({ open: false, orderId: null });
  const [deliveryFailModal, setDeliveryFailModal] = useState({ open: false, orderId: null });
  const [collectCashModal, setCollectCashModal] = useState({ open: false, orderId: null, amount: null });
  const [expandedItemRows, setExpandedItemRows] = useState({});

  const ITEMS_PREVIEW_COUNT = 3;

  const toggleItemsExpanded = (orderId) => {
    setExpandedItemRows((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const formatOrderedAt = (order) => {
    const seconds = order?.createdAt?._seconds;
    if (!seconds) return order?.orderedAt || "N/A";
    const date = new Date(seconds * 1000);
    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    }).format(date);
    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    }).format(date);
    return `${datePart}, ${timePart}`;
  };

  const userRole = useSelector((state) => state?.login?.role);

  // Backend now returns a specific message/errorCode for business-rule
  // failures (e.g. "Invalid transition: ...", "Delivery boy not found")
  // instead of a generic 500 — surface it instead of a static fallback.
  const getErrorMessage = (err, fallback) =>
    err.response?.data?.message || err.response?.data?.error || fallback;

  const toggleModal = (orderId) => {
    setSelectedOrder(orderId);
    setModalOpen(!modalOpen);
  };

  // Patches a single order in local state from a mutation's response,
  // instead of re-fetching the whole list/page just to reflect one row's
  // change — that full refetch, repeated after every single
  // accept/status-update/assign/refund action across a full order
  // lifecycle, was the dominant source of Firestore read volume on this page.
  // Patches whichever of the two data sources (paginated browse view, or a
  // search result set) is currently on screen.
  const patchOrderInState = (updatedOrder) => {
    if (!updatedOrder || !updatedOrder.id) return;
    const patch = (list) =>
      list.map((o) => (o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o));
    setPageOrders(patch);
    setSearchResults((prev) => (prev ? patch(prev) : prev));
  };

  const fetchMetrics = async () => {
    try {
      const token = localStorage.getItem("token");
      const resp = await GetOrderMetrics(token);
      // The global axios response interceptor (helpers/api_helper.js)
      // already unwraps response.data, so `resp` here is already
      // {success, data, error} — not a raw axios response.
      if (resp.data) setMetrics(resp.data);
    } catch (err) {
      console.log(err);
    }
  };

  // Fetches one page of the active tab. `silent` skips the full-screen
  // spinner (background refreshes). Uses the cached cursor for `page` if
  // we've already visited it; a target page with no cached cursor yet
  // (e.g. jumping straight to "Last" before visiting anything in between)
  // walks forward fetching+caching each page along the way — still only
  // ever reads `itemsPerPage` documents per request, never the whole tab.
  const fetchPage = async (page, { silent = false, cursorsOverride } = {}) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let cursors = cursorsOverride || pageCursors;
      let cursor = page === 1 ? null : cursors[page];
      let walkedCursors = cursors;

      if (page !== 1 && cursor === undefined) {
        // No cached cursor for this page yet — walk forward from the
        // furthest page we do have, one bounded (itemsPerPage-sized) fetch
        // at a time, caching each cursor as we go.
        const knownPages = Object.keys(walkedCursors).map(Number);
        let fromPage = Math.max(...knownPages);
        let fromCursor = walkedCursors[fromPage];
        for (let p = fromPage; p < page; p++) {
          const resp = await GetOrdersPage(token, {
            tab: activeTabRef.current,
            limit: itemsPerPage,
            cursor: fromCursor,
          });
          const result = resp.data || {};
          fromCursor = result.nextCursor ?? null;
          walkedCursors = { ...walkedCursors, [p + 1]: fromCursor };
          if (p + 1 === page) {
            setPageOrders(result.orders || []);
            setPageTotalCount(result.totalCount || 0);
          }
        }
        setPageCursors(walkedCursors);
        setCurrentPage(page);
        return;
      }

      const resp = await GetOrdersPage(token, { tab: activeTabRef.current, limit: itemsPerPage, cursor });
      const result = resp.data || { orders: [], nextCursor: null, hasMore: false, totalCount: 0 };
      setPageOrders(result.orders);
      setPageTotalCount(result.totalCount || 0);
      if (result.nextCursor !== undefined) {
        setPageCursors((prev) => ({ ...prev, [page + 1]: result.nextCursor }));
      }
      setCurrentPage(page);
    } catch (err) {
      console.log(err);
      if (!silent) toast.error(getErrorMessage(err, "Failed to load orders."));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Reset to page 1 of the new tab whenever it changes.
  useEffect(() => {
    setPageCursors({ 1: null });
    setCurrentPage(1);
    if (!searchQuery.trim()) {
      fetchPage(1, { cursorsOverride: { 1: null } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  // Search bypasses pagination entirely — a dedicated full (still
  // server-capped at 500) fetch, debounced so it only fires once typing
  // pauses rather than on every keystroke. Clearing the box drops back to
  // the paginated browse view at whatever page/tab was active.
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setCurrentPage(1);
      return;
    }
    setSearching(true);
    const debounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const resp =
          userRole === "admin"
            ? await GetMyStoreOrders(token)
            : await GetAllOrders(token);
        setSearchResults(resp.data || []);
        setCurrentPage(1);
      } catch (err) {
        console.log(err);
        toast.error(getErrorMessage(err, "Search failed."));
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, userRole]);

  // Navigates to a page — cheap client-side slice in search mode, a real
  // (bounded) fetch in browse mode.
  const goToPage = (page) => {
    if (searchResults !== null) {
      setCurrentPage(page);
    } else {
      fetchPage(page);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      if (searchResults !== null) {
        const token = localStorage.getItem("token");
        const resp =
          userRole === "admin"
            ? await GetMyStoreOrders(token)
            : await GetAllOrders(token);
        setSearchResults(resp.data || []);
      } else {
        await fetchPage(currentPage);
      }
      await fetchMetrics();
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "Failed to refresh orders."));
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (
        selectedDeliveryBoyName === "" ||
        selectedDeliveryBoyPhoneNumber === ""
      ) {
        setLoading(false);
        return toast.error("Please select delivery boy");
      }
      const payload = {
        deliveryBoy: {
          name: selectedDeliveryBoyName,
          phoneNumber: selectedDeliveryBoyPhoneNumber,
        },
      };
      const resp = await AssignDeliveryBoy(token, selectedOrder, payload);
      toast.success("Delivery boy assigned successfully!");
      setModalOpen(false);
      setSelectedDeliveryBoyName("");
      setSelectedDeliveryBoyPhoneNumber("");
      patchOrderInState(resp.data);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "Failed to assign delivery boy."));
    } finally {
      setLoading(false);
    }
  };

  const searchResultsRef = useRef(searchResults);
  searchResultsRef.current = searchResults;

  // `silent` skips the full-screen blocking LoadingSpinner — used for
  // background refreshes (RTDB live signal + the polling fallback) so live
  // updates don't flash a dark overlay over the whole page every time.
  // In search mode this re-runs the (still capped) full search fetch; in
  // browse mode it only re-reads the single page currently on screen —
  // either way, a world apart from re-fetching the whole order list on
  // every tick, which is what this used to do.
  const refreshSilently = async () => {
    try {
      if (searchResultsRef.current !== null) {
        const token = localStorage.getItem("token");
        const resp =
          userRole === "admin"
            ? await GetMyStoreOrders(token)
            : await GetAllOrders(token);
        setSearchResults(resp.data || []);
      } else {
        await fetchPage(currentPageRef.current, { silent: true });
      }
    } catch (err) {
      console.log(err);
    }
    fetchMetrics();
  };

  // Safety-net polling behind the RTDB realtime subscription below: if live
  // updates are ever unavailable (e.g. RTDB security rules deny reading the
  // whole `orders` node for this admin — see firebaseRealtime.js), the table
  // still refreshes on its own instead of needing a manual Refresh. Each
  // tick is still a real Firestore read, so this being a rare-case fallback
  // (not the primary update path) means it should stay infrequent — but
  // it's now bounded to one page (or the search's capped fetch) instead of
  // the whole order list regardless.
  useEffect(() => {
    const id = setInterval(() => {
      refreshSilently();
    }, 90000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [liveConnected, setLiveConnected] = useState(false);

  // Live table updates: RTDB's orders/{id}/status is a change signal only
  // (StatusBroadcastService writes {status, updatedAt}, not a full order),
  // so on every add/change we just re-fetch (now: just the current page,
  // not the whole list) rather than try to merge partial RTDB data into
  // state. Debounced because `child_added` fires once per existing order
  // the moment the listener attaches, plus bursts (e.g. rider assignment
  // can touch two orders at once) would otherwise trigger a refetch per
  // event.
  useEffect(() => {
    let debounceTimer = null;
    const unsubscribe = subscribeToOrderChanges(() => {
      setLiveConnected(true);
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        refreshSilently();
      }, 1200);
    });
    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const UpdateStatus = async (id, data) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await StatusUpdate(id, data, token);
      toast.success(`Order status updated to ${data.status}`);
      patchOrderInState(resp.data);
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "Failed to update order status."));
    } finally {
      setLoading(false);
    }
  };

  const TogglePaymentStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === "PAID" ? "PENDING" : "PAID";
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await UpdatePaymentStatus(id, nextStatus, token);
      toast.success(`Payment status updated to ${nextStatus}`);
      patchOrderInState(resp.data);
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "Failed to update payment status."));
    } finally {
      setLoading(false);
    }
  };

  // Going PENDING -> PAID on a COD order means "the driver/store actually
  // has the cash in hand" — worth a confirmation, unlike the reverse
  // (PAID -> PENDING), which is just an admin correcting a mistake.
  const handleCollectCashConfirm = async () => {
    await TogglePaymentStatus(collectCashModal.orderId, "PENDING");
    setCollectCashModal({ open: false, orderId: null, amount: null });
  };

  const handleProcessRefund = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await ProcessRefund(token, id);
      toast.success("Refund processed successfully!");
      patchOrderInState(resp.data);
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "Failed to process refund."));
    } finally {
      setLoading(false);
    }
  };

  const GetDeliverBoys = async () => {
    try {
      // GetAvailableDeliveryPart is now token-scoped server-side — a
      // store-tied admin automatically gets only their own store's
      // available riders, superadmin gets all of them. No more branching
      // needed here (the old store-admin branch called a broken
      // username-matching endpoint — see requests.js).
      const token = localStorage.getItem("token");
      const resp = await GetAvailableDeliveryPart(token);
      setAvailableDeliveryBoys(resp.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    GetDeliverBoys();
  }, []);

  const handleDeliveryBoyChange = (e) => {
    if (!e.target.value) {
      setSelectedDeliveryBoyName("");
      setSelectedDeliveryBoyPhoneNumber("");
      return;
    }
    const selectedDeliveryBoyData = JSON.parse(e.target.value);
    const { name, phoneNumber } = selectedDeliveryBoyData;
    setSelectedDeliveryBoyName(name);
    setSelectedDeliveryBoyPhoneNumber(phoneNumber);
  };

  // ── Reason modal handlers ──
  const handleRejectConfirm = ({ reason, comments }) => {
    UpdateStatus(rejectModal.orderId, { status: "rejected", reason, comments });
    setRejectModal({ open: false, orderId: null });
  };

  const handleCancelConfirm = ({ reason, comments }) => {
    UpdateStatus(cancelModal.orderId, { status: "cancelled", reason, comments });
    setCancelModal({ open: false, orderId: null });
  };

  const handleDeliveryFailConfirm = ({ reason, comments }) => {
    UpdateStatus(deliveryFailModal.orderId, { status: "delivery_failed", reason, comments });
    setDeliveryFailModal({ open: false, orderId: null });
  };

  // Metrics cards — always from the aggregation-based `metrics` state
  // (cheap regardless of how many orders exist), independent of whatever
  // tab/search is currently active.
  const { totalOrders, newOrders, preparingOrders, liveOrders, totalRevenue } = metrics;

  // Search mode: same client-side filter this page always used, just now
  // scoped to only run while actively searching (searchResults is the
  // capped-at-500 fetch from the search effect above).
  const searchFilteredOrders =
    searchResults !== null
      ? searchResults.filter((order) => {
          const matchesSearch =
            (order.orderID || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (order.customerName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (order.store?.name || "").toLowerCase().includes(searchQuery.toLowerCase());

          let matchesTab = true;
          const status = (order.status || "").toLowerCase();
          if (activeTab === "ordered") {
            matchesTab = ["ordered", "received"].includes(status);
          } else if (activeTab === "preparing") {
            matchesTab = ["accepted", "preparing", "ready", "assigned", "on the way", "out_for_delivery", "picked_up", "reattempt"].includes(status);
          } else if (activeTab === "delivered") {
            matchesTab = status === "delivered";
          } else if (activeTab === "rejected") {
            matchesTab = ["rejected", "cancelled", "canceled"].includes(status);
          } else if (activeTab === "live") {
            matchesTab = !LIVE_ORDER_TERMINAL_STATUSES.includes(status);
          }

          return matchesSearch && matchesTab;
        })
      : null;

  // Pagination: server-driven page (pageOrders/pageTotalCount) in browse
  // mode, client-side slice over the bounded search result set in search
  // mode.
  const isSearching = searchResults !== null;
  const totalItems = isSearching ? searchFilteredOrders.length : pageTotalCount;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = isSearching
    ? searchFilteredOrders.slice(indexOfFirstItem, indexOfLastItem)
    : pageOrders;

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className={`page-content ${classes.dashboard}`}>
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Orders" />

          {/* ─── METRICS CARDS ────────────────────────────────────────── */}
          <div className={classes.metricsGrid}>
            <div className={classes.metricCard}>
              <div className={classes.metricInfo}>
                <h4>Total Orders</h4>
                <p className={classes.metricValue}>{totalOrders}</p>
              </div>
              <div className={`${classes.metricIcon} ${classes.iconBlue}`}>
                <i className="mdi mdi-basket-outline"></i>
              </div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricInfo}>
                <h4>Awaiting Acceptance</h4>
                <p className={classes.metricValue}>{newOrders}</p>
              </div>
              <div className={`${classes.metricIcon} ${classes.iconOrange}`}>
                <i className="mdi mdi-bell-ring-outline"></i>
              </div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricInfo}>
                <h4>Accepted &amp; Preparing</h4>
                <p className={classes.metricValue}>{preparingOrders}</p>
              </div>
              <div className={`${classes.metricIcon} ${classes.iconPurple}`}>
                <i className="mdi mdi-clock-outline"></i>
              </div>
            </div>

            <div className={classes.metricCard}>
              <div className={classes.metricInfo}>
                <h4>Total Sales</h4>
                <p className={classes.metricValue}>₹{totalRevenue.toLocaleString()}</p>
              </div>
              <div className={`${classes.metricIcon} ${classes.iconGreen}`}>
                <i className="mdi mdi-currency-inr"></i>
              </div>
            </div>
          </div>

          {/* ─── CONTROLS ROW: SEARCH & TABS ───────────────────────────── */}
          <div className={classes.controlsRow}>
            <div className={classes.searchWrapper}>
              <i className={`mdi mdi-magnify ${classes.searchIcon}`}></i>
              <input
                type="text"
                placeholder="Search by Order ID, customer or store..."
                className={classes.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className={classes.tabsList}>
              <button
                className={`${classes.tabBtn} ${activeTab === "all" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("all")}
              >
                All
              </button>
              <button
                className={`${classes.tabBtn} ${activeTab === "live" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("live")}
              >
                🔴 Live ({liveOrders})
              </button>
              <button
                className={`${classes.tabBtn} ${activeTab === "ordered" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("ordered")}
              >
                New ({newOrders})
              </button>
              <button
                className={`${classes.tabBtn} ${activeTab === "preparing" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("preparing")}
              >
                Preparing
              </button>
              <button
                className={`${classes.tabBtn} ${activeTab === "delivered" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("delivered")}
              >
                Delivered
              </button>
              <button
                className={`${classes.tabBtn} ${activeTab === "rejected" ? classes.tabBtnActive : ""}`}
                onClick={() => setActiveTab("rejected")}
              >
                Rejected
              </button>
              <button
                onClick={handleRefresh}
                className={`btn btn-primary ${classes.refreshBtn}`}
              >
                <i className="mdi mdi-refresh"></i> Refresh
              </button>
              {liveConnected && (
                <span
                  title="Table updates automatically when an order's status changes"
                  style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#22c55e", fontWeight: 600, marginLeft: "8px" }}
                >
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#22c55e", display: "inline-block" }}></span>
                  Live
                </span>
              )}
            </div>
          </div>

          {/* ─── ORDERS LIST TABLE ────────────────────────────────────── */}
          <div className={classes.ordersTableCard}>
            {totalItems === 0 ? (
              <div className={classes.emptyState}>
                <i className="mdi mdi-basket-outline"></i>
                <h3>No Orders Found</h3>
                <p>No orders match the selected filters or search criteria.</p>
              </div>
            ) : (
              <>
                <table className={classes.customTable}>
                  <thead>
                    <tr>
                      <th>OrderId</th>
                      <th>Ordered At</th>
                      <th>Ordered By</th>
                      <th>Item(s)</th>
                      <th>Address</th>
                      <th>Price Paid</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((order) => {
                      const statusLower = (order.status || "").toLowerCase();
                      let statusClass = classes.badgeCustom;
                      if (statusLower === "ordered") statusClass += ` ${classes.badgeOrdered}`;
                      else if (statusLower === "preparing" || statusLower === "reattempt") statusClass += ` ${classes.badgePreparing}`;
                      else if (statusLower === "on the way" || statusLower === "assigned" || statusLower === "picked_up" || statusLower === "out_for_delivery") statusClass += ` ${classes.badgeOnTheWay}`;
                      else if (statusLower === "delivered") statusClass += ` ${classes.badgeDelivered}`;
                      else if (statusLower === "rejected" || statusLower === "cancelled") statusClass += ` ${classes.badgeRejected}`;

                      const methodClass = `${classes.badgeCustom} ${order.method === "delivery" ? classes.badgeDelivery : classes.badgePickup}`;

                      return (
                        <tr key={order.id}>
                          <td>
                            <span className={classes.orderIdBadge}>{order.orderID || "N/A"}</span>
                            <span className={classes.storeName}>{order?.store?.name || "N/A"}</span>
                          </td>
                          <td>{formatOrderedAt(order)}</td>
                          <td>
                            <span className={classes.customerName}>{order.customerName}</span>
                            <span className={classes.customerPhone}>{order.phoneNumber}</span>
                          </td>
                          <td>
                            {(() => {
                              const items = order.items || [];
                              const isExpanded = !!expandedItemRows[order.id];
                              const visibleItems = isExpanded ? items : items.slice(0, ITEMS_PREVIEW_COUNT);
                              const hiddenCount = items.length - ITEMS_PREVIEW_COUNT;
                              return (
                                <>
                                  <ul className={classes.itemsList}>
                                    {visibleItems.map((item, idx) => (
                                      <li key={idx}>
                                        {item.name} ({item.quantity})
                                      </li>
                                    ))}
                                  </ul>
                                  {hiddenCount > 0 && (
                                    <button
                                      className={classes.itemsToggleBtn}
                                      onClick={() => toggleItemsExpanded(order.id)}
                                    >
                                      {isExpanded ? "Show less" : `+${hiddenCount} more`}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td>
                            <span className={`${methodClass} ${classes.methodBadgeInline}`}>{order.method}</span>
                            <div className={classes.addressText}>{order.customerAddress}</div>
                          </td>
                          <td>
                            <div className={classes.priceContainer}>
                              <span className={classes.priceCol}>₹{order.totalprice}</span>
                              <div className={classes.paymentInfo}>
                                <span className={`${classes.badgeCustom} ${order.paymentMethod === "ONLINE" ? classes.badgeOnline : classes.badgeCod}`}>
                                  {order.paymentMethod || "COD"}
                                </span>
                                {!["cancelled", "canceled", "rejected", "delivery_failed"].includes(statusLower) && (
                                  <>
                                    <span className={`${classes.paymentStatusText} ${order.paymentStatus === "PAID" ? classes.statusPaid : classes.statusPending}`}>
                                      {order.paymentStatus || "PENDING"}
                                    </span>
                                    {order.paymentMethod !== "ONLINE" && (
                                      <button
                                        className={classes.paymentToggleBtn}
                                        title="Manually override payment status (use if the delivery boy couldn't update it)"
                                        onClick={() => {
                                          if ((order.paymentStatus || "PENDING") === "PAID") {
                                            TogglePaymentStatus(order.id, order.paymentStatus);
                                          } else {
                                            setCollectCashModal({ open: true, orderId: order.id, amount: order.totalprice });
                                          }
                                        }}
                                      >
                                        Mark as {order.paymentStatus === "PAID" ? "Pending" : "Paid"}
                                      </button>
                                    )}
                                  </>
                                )}
                                {order.refund && order.refund.required && (
                                  <>
                                    <span className={classes.refundText}>
                                      Refund: {order.refund.status}
                                    </span>
                                    {(order.refund.status === "INITIATED" || order.refund.status === "FAILED") && (
                                      <button
                                        className={classes.paymentToggleBtn}
                                        title="Fires the actual Razorpay refund — moves real money"
                                        onClick={() => handleProcessRefund(order.id)}
                                      >
                                        {order.refund.status === "FAILED" ? "Retry Refund" : "Process Refund"}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={statusClass}>{order.status}</span>
                          </td>
                          <td>
                            <div className={classes.actionCell}>
                              {/* ── New / Received ── */}
                              {["received", "ordered"].includes(statusLower) && (
                                <>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnAccept}`}
                                    onClick={() => UpdateStatus(order.id, { status: "accepted" })}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnReject}`}
                                    onClick={() => setRejectModal({ open: true, orderId: order.id })}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}

                              {/* ── Accepted ── */}
                              {statusLower === "accepted" && (
                                <button
                                  className={`${classes.btnAction} ${classes.btnAccept}`}
                                  onClick={() => UpdateStatus(order.id, { status: "preparing" })}
                                >
                                  Prepare
                                </button>
                              )}

                              {/* ── Preparing: Mark Ready + Cancel ── */}
                              {statusLower === "preparing" && (
                                <>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnAccept}`}
                                    onClick={() => UpdateStatus(order.id, { status: "ready" })}
                                  >
                                    Mark Ready
                                  </button>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnCancel}`}
                                    onClick={() => setCancelModal({ open: true, orderId: order.id })}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}

                              {/* ── Ready ── */}
                              {statusLower === "ready" && (
                                <>
                                  {order.method === "delivery" ? (
                                    <button
                                      className={`${classes.btnAction} ${classes.btnAssign}`}
                                      onClick={() => toggleModal(order.id)}
                                    >
                                      Assign
                                    </button>
                                  ) : (
                                    <button
                                      className={`${classes.btnAction} ${classes.btnDeliver}`}
                                      onClick={() => UpdateStatus(order.id, { status: "delivered" })}
                                    >
                                      Deliver
                                    </button>
                                  )}
                                </>
                              )}

                              {/* ── Assigned: -> picked_up ── */}
                              {statusLower === "assigned" && (
                                <button
                                  className={`${classes.btnAction} ${classes.btnAssign}`}
                                  onClick={() => UpdateStatus(order.id, { status: "picked_up" })}
                                >
                                  Picked Up
                                </button>
                              )}

                              {/* ── Picked Up: -> out_for_delivery ── */}
                              {statusLower === "picked_up" && (
                                <button
                                  className={`${classes.btnAction} ${classes.btnAssign}`}
                                  onClick={() => UpdateStatus(order.id, { status: "out_for_delivery" })}
                                >
                                  Out for Delivery
                                </button>
                              )}

                              {/* ── Out for Delivery: Deliver + Delivery Failed ──
                                  "on the way" is the rider app's own name for this
                                  same phase (STATUS_TRANSITIONS treats both as
                                  equivalent — 'on the way' is the legacy alias
                                  for 'out_for_delivery') — without matching it
                                  here too, an order a rider moved to "on the way"
                                  (via the rider app's own status buttons, not
                                  admin's) showed no action button at all once it
                                  reached this stage. ── */}
                              {(statusLower === "out_for_delivery" || statusLower === "on the way") && (
                                <>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnDeliver}`}
                                    onClick={() => UpdateStatus(order.id, { status: "delivered" })}
                                  >
                                    Deliver
                                  </button>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnReject}`}
                                    onClick={() => setDeliveryFailModal({ open: true, orderId: order.id })}
                                  >
                                    Fail
                                  </button>
                                </>
                              )}

                              {/* ── Delivery Failed: Reattempt + Close ── */}
                              {statusLower === "delivery_failed" && !order.deliveryClosed && (
                                <>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnAccept}`}
                                    onClick={() => UpdateStatus(order.id, { status: "reattempt" })}
                                  >
                                    Reattempt
                                  </button>
                                  <button
                                    className={`${classes.btnAction} ${classes.btnCancel}`}
                                    onClick={() => UpdateStatus(order.id, { status: "delivery_failed", deliveryClosed: true })}
                                  >
                                    Close
                                  </button>
                                </>
                              )}

                              {/* ── Reattempt: -> Assign or Deliver ── */}
                              {statusLower === "reattempt" && (
                                <>
                                  {order.method === "delivery" ? (
                                    <button
                                      className={`${classes.btnAction} ${classes.btnAssign}`}
                                      onClick={() => toggleModal(order.id)}
                                    >
                                      Assign
                                    </button>
                                  ) : (
                                    <button
                                      className={`${classes.btnAction} ${classes.btnDeliver}`}
                                      onClick={() => UpdateStatus(order.id, { status: "delivered" })}
                                    >
                                      Deliver
                                    </button>
                                  )}
                                </>
                              )}

                              {/* ── Rejected or Cancelled: Show Reason ── */}
                              {["rejected", "cancelled", "canceled"].includes(statusLower) && (
                                <div className={classes.reasonContainer}>
                                  <span className={classes.reasonLabel}>
                                    {statusLower === "rejected" ? "Rejection Reason" : "Cancellation Reason"}
                                  </span>
                                  <span className={classes.reasonValue}>
                                    {order.statusReason?.message || order.reason || "No reason specified"}
                                  </span>
                                  {order.comments && (
                                    <span className={classes.reasonComments}>
                                      Comments: {order.comments}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* ─── PAGINATION CONTROLS FOOTER ────────────────────────── */}
                {totalPages > 1 && (
                  <div className={classes.paginationRow}>
                    <div className={classes.paginationInfo}>
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                    </div>
                    <div className={classes.paginationGroup}>
                      <button
                        className={classes.pageBtn}
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </button>
                      <button
                        className={classes.pageBtn}
                        onClick={() => goToPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                        })
                        .map((page, index, array) => {
                          const elements = [];
                          if (index > 0 && page - array[index - 1] > 1) {
                            elements.push(
                              <span key={`ellipses-${page}`} className="mx-1 align-self-center text-muted">
                                ...
                              </span>
                            );
                          }
                          elements.push(
                            <button
                              key={page}
                              className={`${classes.pageBtn} ${currentPage === page ? classes.pageBtnActive : ""}`}
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          );
                          return elements;
                        })}

                      <button
                        className={classes.pageBtn}
                        onClick={() => goToPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                      <button
                        className={classes.pageBtn}
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ─── ASSIGN DELIVERY MODAL ─────────────────────────────────── */}
          <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
            <ModalHeader toggle={toggleModal}>Assign Delivery Rider</ModalHeader>
            <ModalBody style={{ maxHeight: "400px", overflowY: "auto" }}>
              <div className="text-muted mb-3" style={{ fontSize: "13px" }}>
                Select an available rider for order assignment:
              </div>
              {AvailableDeliveryBoys.length > 0 ? (
                AvailableDeliveryBoys.map((rider, index) => {
                  const isSelected = selectedDeliveryBoyPhoneNumber === rider.phoneNumber;
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        setSelectedDeliveryBoyName(rider.name);
                        setSelectedDeliveryBoyPhoneNumber(rider.phoneNumber);
                      }}
                      className="d-flex align-items-center justify-content-between p-3 mb-2 rounded border"
                      style={{
                        cursor: "pointer",
                        borderColor: isSelected ? "#f97316" : "#e5e7eb",
                        backgroundColor: isSelected ? "#fff7ed" : "#ffffff",
                        transition: "all 0.2s ease"
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            backgroundColor: "#22c55e",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "13px"
                          }}
                        >
                          {rider.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h6 className="m-0 font-weight-bold" style={{ color: isSelected ? "#ea580c" : "#1f2937" }}>
                            {rider.name}
                          </h6>
                          <small className="text-muted">
                            {rider.vehicle_type || "Bike"} • {rider.phoneNumber}
                          </small>
                        </div>
                      </div>
                      <div>
                        <input
                          type="radio"
                          name="selectedRider"
                          checked={isSelected}
                          onChange={() => {}} // handled by div click
                          style={{ accentColor: "#f97316", width: "16px", height: "16px" }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-4 text-muted border border-dashed rounded bg-light">
                  No available riders for this store at the moment.
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="primary" style={{ backgroundColor: "#f97316", borderColor: "#f97316" }} onClick={handleAssign}>
                Assign Rider
              </Button>{" "}
              <Button color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          {/* ─── REJECT ORDER MODAL ────────────────────────────────────── */}
          <ReasonModal
            isOpen={rejectModal.open}
            toggle={() => setRejectModal({ open: false, orderId: null })}
            title="Reject Order"
            reasons={REJECT_REASONS}
            commentsLabel="Comments"
            commentsRequired={false}
            onConfirm={handleRejectConfirm}
            confirmLabel="Reject Order"
            confirmColor="#e31b23"
          />

          {/* ─── CANCEL ORDER MODAL ────────────────────────────────────── */}
          <ReasonModal
            isOpen={cancelModal.open}
            toggle={() => setCancelModal({ open: false, orderId: null })}
            title="Cancel Order"
            reasons={CANCEL_REASONS}
            commentsLabel="Comments"
            commentsRequired={false}
            onConfirm={handleCancelConfirm}
            confirmLabel="Cancel Order"
            confirmColor="#6c757d"
          />

          {/* ─── DELIVERY FAILED MODAL ─────────────────────────────────── */}
          <ReasonModal
            isOpen={deliveryFailModal.open}
            toggle={() => setDeliveryFailModal({ open: false, orderId: null })}
            title="Delivery Failed"
            reasons={DELIVERY_FAILED_REASONS}
            commentsLabel="Comments"
            commentsRequired={false}
            onConfirm={handleDeliveryFailConfirm}
            confirmLabel="Mark as Failed"
            confirmColor="#e31b23"
          />

          {/* ─── MARK CASH COLLECTED MODAL ─────────────────────────────── */}
          <Modal
            isOpen={collectCashModal.open}
            toggle={() => setCollectCashModal({ open: false, orderId: null, amount: null })}
            centered
            size="sm"
          >
            <ModalHeader
              toggle={() => setCollectCashModal({ open: false, orderId: null, amount: null })}
              style={{ borderBottom: "2px solid #f1f3f5" }}
            >
              <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>Mark Cash Collected</span>
            </ModalHeader>
            <ModalBody style={{ padding: "1.5rem" }}>
              <p style={{ margin: 0, color: "#495057" }}>
                Confirm that ₹{collectCashModal.amount} in cash has actually been collected for
                this order before marking it as paid.
              </p>
            </ModalBody>
            <ModalFooter style={{ borderTop: "2px solid #f1f3f5", gap: "0.5rem" }}>
              <Button
                onClick={handleCollectCashConfirm}
                style={{
                  backgroundColor: "#28a745",
                  borderColor: "#28a745",
                  fontWeight: 700,
                  borderRadius: "10px",
                  padding: "0.5rem 1.5rem",
                }}
              >
                Confirm Collected
              </Button>
              <Button
                color="secondary"
                onClick={() => setCollectCashModal({ open: false, orderId: null, amount: null })}
                style={{ borderRadius: "10px", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default Orders;
